// src/Home.jsx
import { useEffect, useMemo, useState, useContext } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Hero from "./Hero";
import CategoryBar from "./CategoryBar";
import ToolGrid from "./ToolGrid";
import Footer from "./Footer";
import { db } from "./firebase";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import SearchContext from "./SearchContext";
// SEO: Import SEO utilities for dynamic meta tags
import {
  getHomepageTitle,
  getHomepageDescription,
  getCategoryTitle,
  getCategoryDescription,
  getCategorySEO,
  updateMetaTags,
  getCanonicalUrl,
  trackPageView,
} from "./utils/seo";

export default function Home() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { search, setSearch, searchRef } = useContext(SearchContext);
  const [category, setCategory] = useState("All");
  const [searchParams] = useSearchParams();

  // Handle category from URL query param
  useEffect(() => {
    const categoryParam = searchParams.get("category");
    if (categoryParam) {
      setCategory(categoryParam);
    }
  }, [searchParams]);

  // SEO: Update meta tags based on current category
  useEffect(() => {
    if (category === "All") {
      // Homepage SEO
      updateMetaTags({
        title: getHomepageTitle(),
        description: getHomepageDescription(),
        canonicalUrl: getCanonicalUrl("/"),
      });
      trackPageView("/", getHomepageTitle());
    } else {
      // Category page SEO
      updateMetaTags({
        title: getCategoryTitle(category),
        description: getCategoryDescription(category),
        canonicalUrl: getCanonicalUrl(`/?category=${encodeURIComponent(category)}`),
      });
      trackPageView(`/?category=${category}`, getCategoryTitle(category));
    }
  }, [category]);

  // Firestore realtime
  useEffect(() => {
    const q = query(collection(db, "tools"), orderBy("name"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setTools(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
        );
        setLoading(false);
      },
      () => {
        setError("Could not load tools. Please try again later.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Search + Filtering
  const filteredTools = useMemo(() => {
    const s = search.trim().toLowerCase();

    return tools.filter((tool) => {
      const categoryMatch =
        category === "All" ||
        (tool.category &&
          tool.category.toLowerCase() === category.toLowerCase());

      if (!categoryMatch) return false;

      if (!s) return true;

      const haystack = (
        (tool.name || "") +
        " " +
        (tool.description || "") +
        " " +
        (tool.category || "") +
        " " +
        (tool.keywords || "")
      ).toLowerCase();

      return haystack.includes(s);
    });
  }, [tools, search, category]);

  return (
    <div className="min-h-screen bg-white text-neutral-900 dark:bg-neutral-950 dark:text-neutral-50 transition-colors duration-300">

      <Hero />

      {/* MAIN SEARCH */}
      <section className="max-w-4xl mx-auto px-4 mt-6 flex flex-col items-center">
        <input
          id="main-search-input"
          ref={searchRef}
          value={search}
          onChange={(e) => setSearch(e.target.value.toLowerCase())}
          placeholder="Search tools by name, use-case, or category…"
          className="
            w-full max-w-2xl px-5 py-3 rounded-full
            bg-white border border-neutral-300
            text-neutral-900 placeholder-neutral-500

            dark:bg-neutral-900 dark:border-neutral-700
            dark:text-neutral-100 dark:placeholder-neutral-600

            focus:ring-2 focus:ring-lime-500/60
            outline-none transition shadow-md
          "
        />
      </section>

      <CategoryBar
        activeCategory={category}
        onCategoryChange={setCategory}
      />

      {/* SEO: Category heading, description, and count for category pages */}
      {category !== "All" && (
        <section className="max-w-4xl mx-auto px-4 mt-6 mb-4">
          <h1 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white mb-2">
            Best {getCategorySEO(category).displayName} & Alternatives
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed mb-2">
            {getCategorySEO(category).description}
          </p>
          <p className="text-sm text-neutral-500 dark:text-neutral-500">
            Showing {filteredTools.length} curated {getCategorySEO(category).displayName.toLowerCase()}
          </p>
        </section>
      )}

      {/* SEO: Homepage H1 for "All" category */}
      {category === "All" && (
        <section className="max-w-4xl mx-auto px-4 mt-6 mb-4 text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white mb-2">
            Discover the Best AI, Developer & Productivity Tools
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed">
            Curated directory of {tools.length}+ free & paid tools. Find alternatives, compare features, and boost your workflow.
          </p>
        </section>
      )}

      {/* Featured Collection Banner - GTA 6 Creator Tools */}
      {/* NOTE: This is a temporary, hype-aware feature. Remove when no longer relevant. */}
      {category === "All" && (
        <section className="max-w-4xl mx-auto px-4 mb-4">
          <Link
            to="/collections/gta-6-tools"
            className="
              block p-4 rounded-xl
              bg-gradient-to-r from-orange-500/10 via-amber-500/10 to-yellow-500/10
              dark:from-orange-500/20 dark:via-amber-500/20 dark:to-yellow-500/20
              border border-orange-300/30 dark:border-orange-500/30
              hover:border-orange-400/50 dark:hover:border-orange-400/50
              hover:shadow-lg transition-all
              group
            "
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🎮</span>
                <div>
                  <p className="font-semibold text-neutral-800 dark:text-neutral-100 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition">
                    Tools for GTA 6 Creators
                  </p>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    Video editing, thumbnails, AI writing & streaming tools
                  </p>
                </div>
              </div>
              <span className="text-neutral-400 group-hover:text-orange-500 transition">→</span>
            </div>
          </Link>
        </section>
      )}

      <main className="max-w-6xl mx-auto px-4 pb-16 pt-4">
        {loading && (
          <p className="mt-10 text-center text-neutral-400">
            Loading tools…
          </p>
        )}

        {error && (
          <p className="mt-10 text-center text-red-400">{error}</p>
        )}

        {!loading && !error && filteredTools.length === 0 && (
          <p className="mt-10 text-center text-neutral-400">
            No tools found. Try another search.
          </p>
        )}

        {!loading && !error && filteredTools.length > 0 && (
          <ToolGrid tools={filteredTools} />
        )}

        {/* SEO: Submit Tool CTA - encourages organic growth */}
        <section className="mt-16 text-center">
          <div className="inline-block p-6 rounded-2xl bg-gradient-to-br from-neutral-100 to-neutral-50 dark:from-neutral-900 dark:to-neutral-800 border border-neutral-200 dark:border-neutral-700 shadow-lg">
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-2">
              Know a great tool we're missing?
            </p>
            <Link
              to="/suggest"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-lime-400 to-emerald-500 text-black font-semibold hover:shadow-lg hover:scale-105 transition-all"
            >
              <span>📝</span> Submit a Tool
            </Link>
            <p className="mt-3 text-xs text-neutral-400 dark:text-neutral-500">
              ✨ Curated & updated regularly
            </p>
          </div>
        </section>

        {/* SEO: Trust & Authority Signals */}
        <section className="mt-8 text-center">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-neutral-500 dark:text-neutral-400">
            <span className="flex items-center gap-1">
              ✓ Curated manually
            </span>
            <span className="flex items-center gap-1">
              ✓ No pirated tools
            </span>
            <span className="flex items-center gap-1">
              ✓ Updated regularly
            </span>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
